/**
 * ImpersonationEntry — /portal/as/:key : l'onglet portail ouvert par l'admin récupère le jeton déposé (usage unique),
 * l'installe pour CET onglet seulement, charge la session portail SANS recharger la page (Safari iOS peut perdre une
 * écriture sessionStorage faite juste avant une navigation) puis passe au tableau de bord — Batch #14.338.
 * /portal/as/failed?d=… : la session « en tant que » a été refusée par le serveur (détail affiché, jamais de boucle vers le login).
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ShieldAlert, Loader2 } from "lucide-react";
import { takeHandoff } from "@/lib/portalSession";
import { useAuth } from "@/contexts/AuthContext";

export default function ImpersonationEntry() {
  const { key } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { refreshPortalSession } = useAuth();
  const [failed, setFailed] = useState(key === "failed" ? (sp.get("d") || "Jeton refusé") : "");
  const ran = useRef(false);

  useEffect(() => {
    if (key === "failed" || ran.current) return;
    ran.current = true;
    const d = takeHandoff(key);
    if (!d) { setFailed("expired"); return; }
    refreshPortalSession().then((r) => {
      if (r.ok) navigate("/portal/dashboard", { replace: true });
      else setFailed(r.detail || "Jeton refusé");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="impersonation-entry">
      {failed ? (
        <div className="max-w-md bg-white border border-red-200 rounded-2xl p-6 text-center space-y-3">
          <ShieldAlert className="w-10 h-10 text-red-600 mx-auto" />
          <h1 className="text-lg font-bold text-slate-900" data-testid="impersonation-entry-title">
            {failed === "expired" ? "Lien de navigation expiré" : "Session « en tant que » refusée"}
          </h1>
          <p className="text-sm text-slate-600">
            {failed === "expired"
              ? "Ce lien « Naviguer en tant que » a déjà été utilisé ou a expiré (2 minutes). Relancez-le depuis l'administration."
              : "Le serveur a refusé le jeton temporaire. Relancez « Naviguer en tant que » depuis l'administration."}
          </p>
          {failed !== "expired" && <p className="text-xs font-mono text-red-700 bg-red-50 rounded-lg p-2 break-words" data-testid="impersonation-entry-detail">{failed}</p>}
          {/* lien « dur » (rechargement complet) : sur /portal/* le contexte ignore la session admin, un <Link> enverrait au login admin */}
          <a href="/admin/portail-clients" className="inline-block text-sm font-semibold text-blue-700 hover:underline" data-testid="impersonation-entry-back">Revenir à l'administration</a>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-slate-600 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Ouverture du portail du client…</div>
      )}
    </div>
  );
}
